/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events -- This is legacy code which applies click handlers to all manner of elements which should not have click handlers. */
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import type { VoidFunctionComponent } from 'react';
import { connect } from 'react-redux';
import type { Dispatch } from 'redux';
import { Action } from '../../../../../typings/permissions';
import { userHasPermissionForTeamProjects, userHasPermissionToEditTeamUsers } from '../../../../helpers/permissions';
import { totalProjectCount } from '../../../../helpers/teams';
import type { ReduxStore } from '../../../../redux/store';
import type { TeamsStore } from '../../../../redux/teams';
import RolesModule from '../../../../redux/roles';
import TeamsModule from '../../../../redux/teams';
import OrganizationsModule from '../../../../redux/organizations';
import type { UsersStore } from '../../../../redux/users';
import UsersModule from '../../../../redux/users';
import type { IAsyncStatus, Organization, RouterProps, Team, ProjectGroup, Dictionary } from '../../../../types';
import { hasError } from '../../../../utils';
import Header from '../../../shared/settings/SettingsPageHeader';
import { ErrorAlert } from '../../../generic/ErrorAlert';
import Loader from '../../../generic/Loader';
import SearchInput from '../../../generic/SearchInput';
import ModalAddTeamMembers from '../../../modals/ModalAddTeamMembers';
import ModalAddTeamProjects from '../../../modals/ModalAddTeamProjects';
import TeamMembers from './TeamMembers';
import TeamProjects from './TeamProjects';
import ProjectGroupModule from '../../../../redux/project_grouping';
import { useOnMount } from '../../../../hooks';
import { useParams } from '../../../../contexts/RouterContext';
import { window } from '../../../../globals';

type ReduxStateProps = {
  team?: Team;
  status: IAsyncStatus;
  organization?: Organization | null;
  releaseGroupStatus: IAsyncStatus;
  teamUpdatingStatus: TeamsStore['teamUpdatingStatus'];
  updatingUsers: UsersStore['updatingUsers'];
  releaseGroups: Dictionary<ProjectGroup>;
};

type DispatchProps = {
  addMembers: (users: Array<{ id: number; roleId?: number }>) => void;
  addProjects: (projectIds: string[]) => void;
  clearUpdatingUserError: (userId: number | string) => void;
  addReleaseGroups: (releaseGroupIds: number[]) => void;
  fetchTeam: () => void;
  fetchUsers: () => void;
  fetchRoles: () => void;
  fetchReleaseGroups: () => void;
  fetchOrganization: () => void;
};

type TeamSettingsProps = ReduxStateProps & DispatchProps;

const TeamSettings = ({
  organization,
  team,
  status,
  releaseGroupStatus,
  teamUpdatingStatus,
  updatingUsers,
  addMembers,
  addProjects,
  addReleaseGroups,
  clearUpdatingUserError,
  releaseGroups,
  fetchTeam,
  fetchUsers,
  fetchRoles,
  fetchReleaseGroups,
  fetchOrganization,
}: TeamSettingsProps) => {
  //  Fetch the organization, team and release groups on mount
  useOnMount(async () => {
    //  Ensure that the organization is fetched first, as the team and release groups
    await fetchOrganization();
    await Promise.all([
      fetchTeam(),
      fetchRoles(),
      fetchUsers(),
      fetchReleaseGroups(),
    ]);
  });

  const [searchValue, setSearchValue] = useState('');

  const [activeTab, setActiveTab] = useState<'members' | 'projects'>('members');

  const [modalToShow, setModalToShow] = useState<'members' | 'projects' | null>(null);

  const [awaitingUpdatesCount, setAwaitingUpdatesCount] = useState(0);

  useEffect(() => {
    if (modalToShow && awaitingUpdatesCount > 0 && !teamUpdatingStatus.loading) {
      // If the update succeeded, close the modal.
      if (!hasError(teamUpdatingStatus)) {
        setModalToShow(null);
      }
      setAwaitingUpdatesCount(awaitingUpdatesCount - 1);
    }
  }, [modalToShow, awaitingUpdatesCount, teamUpdatingStatus]);

  if (hasError(status)) {
    return <ErrorAlert status={status} withMargin />;
  }

  if (status.loading || !organization || !team) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px' }}>
        <Loader />
      </div>
    );
  }

  const updatingUserErrorStatuses = Object.entries(updatingUsers)
    .map(([userId, stat]) => ({ ...stat, userId }))
    .filter(hasError);

  const canAdd =
    (activeTab === 'members' && userHasPermissionToEditTeamUsers(team.id)) ||
    (activeTab === 'projects' && userHasPermissionForTeamProjects(Action.Add, team.id));

  return (
    <div className="team-settings">
      {updatingUserErrorStatuses.map((errorStatus) => (
        <ErrorAlert
          key={errorStatus.userId}
          status={errorStatus}
          actionButtonText="Dismiss"
          onAction={() => clearUpdatingUserError(errorStatus.userId)}
          withMargin
        />
      ))}
      {hasError(releaseGroupStatus) && <ErrorAlert status={releaseGroupStatus} withMargin />}
      <Header heading={team.name} isPadded>
        <Header.TooltipButton
          id="add-team-members-button"
          showTooltip={!canAdd}
          tooltip={`You are not permitted to add ${activeTab} to this team.`}
          onClick={() => setModalToShow(activeTab)}
          isDisabled={!canAdd}
        >
          Add {activeTab === 'members' ? 'Member' : 'Project'}
        </Header.TooltipButton>
      </Header>
      <ul className="team-settings__sub-tabs">
        <li
          onClick={() => setActiveTab('members')}
          className={classNames('clickable', { active: activeTab === 'members' })}
        >
          <span>Members</span> <small>{team.teamUsers?.length || 0}</small>
        </li>
        <li
          onClick={() => setActiveTab('projects')}
          className={classNames('clickable', { active: activeTab === 'projects' })}
        >
          <span>Projects</span> <small>{totalProjectCount(team)}</small>
        </li>
      </ul>
      <div className="team-settings__search-panel">
        <SearchInput value={searchValue} onChange={(ev) => setSearchValue(ev.target.value)} />
      </div>
      {activeTab === 'members' ? (
        <TeamMembers team={team} search={searchValue} />
      ) : (
        <TeamProjects
          organization={organization}
          team={team}
          search={searchValue}
          releaseGroups={Object.values(releaseGroups)}
        />
      )}
      {modalToShow === 'members' && (
        <ModalAddTeamMembers
          team={team}
          onAddedUsers={(users) => {
            setAwaitingUpdatesCount(awaitingUpdatesCount + 1);
            addMembers(users);
          }}
          onCancel={() => setModalToShow(null)}
        />
      )}
      {modalToShow === 'projects' && (
        <ModalAddTeamProjects
          team={team}
          projects={organization.projects}
          releaseGroups={Object.values(releaseGroups)}
          onDone={async (projects, updatedReleaseGroups) => {
            const isAddingProjects = !!projects.length;
            const isAddingReleaseGroups = !!updatedReleaseGroups.length;
            setAwaitingUpdatesCount(awaitingUpdatesCount + Number(isAddingProjects) + Number(isAddingReleaseGroups));
            try {
              if (isAddingProjects) {
                await addProjects(projects);
              }
              if (isAddingReleaseGroups) {
                await addReleaseGroups(updatedReleaseGroups);
              }
            } catch (err) {
              // Error handled by Redux
            }
          }}
          onCancel={() => setModalToShow(null)}
        />
      )}
    </div>
  );
};

//  Params are only used in the mapState/mapDispatch functions
type ContainerProps = Pick<RouterProps<{ id: string }>, 'params'>;

const mapStateToProps = (state: ReduxStore, ownProps: ContainerProps): ReduxStateProps => ({
  team: TeamsModule.selectors.getTeams(state)[ownProps.params.id],
  status: TeamsModule.selectors.getStatus(state),
  organization: OrganizationsModule.selectors.getCurrentOrganization(state),
  releaseGroupStatus: ProjectGroupModule.selectors.getStatus(state),
  teamUpdatingStatus: TeamsModule.selectors.getTeamUpdatingStatus(state),
  updatingUsers: UsersModule.selectors.getUpdatingUsers(state),
  releaseGroups: ProjectGroupModule.selectors.getEntities(state),
});

const mapDispatchToProps = (dispatch: Dispatch, { params }: ContainerProps): DispatchProps => ({
  fetchOrganization: () => dispatch(OrganizationsModule.actions.fetchOne(window.user?.organizationId as number)),
  fetchUsers: () => dispatch(UsersModule.actions.fetchUsersByOrganization(window.user?.organizationId as number)),
  fetchTeam: () => dispatch(TeamsModule.actions.fetchOne(params.id as any as number)), // cast is safe as it is concated to a string in the URL anyway
  fetchRoles: () => dispatch(RolesModule.actions.fetchAll()),
  fetchReleaseGroups: () => dispatch(ProjectGroupModule.actions.fetchAll({})),
  addMembers: (users: Array<{ id: number; roleId?: number }>) =>
    dispatch(TeamsModule.actions.addTeamUsers(params.id, users)),
  addProjects: (projectIds: string[]) =>
    dispatch(TeamsModule.actions.addTeamProjects(params.id, projectIds)),
  clearUpdatingUserError: (userId: number | string) =>
    dispatch(UsersModule.actions.clearUpdatingUserError(userId)),
  addReleaseGroups: (releaseGroupIds: number[]) =>
    dispatch(TeamsModule.actions.addTeamReleaseGroups(params.id, releaseGroupIds)),
});

//  This is the part that is connected to Redux. The container component below is "above"
//  this component in the tree, because it provides the `params.id` prop as an "own prop"
//  that can be read and used in the mapState/mapDispatch functions. above.
const ConnectedTeamSettings = connect(mapStateToProps, mapDispatchToProps)(TeamSettings);

/**
 * Container which ernsures that the org is fetched and passed down to the component,
 * as well as grabbing the route params and providing them, so that
 * this component doens't require being a direct child of the <Route> to work.
 */
const TeamSettingsContainer: VoidFunctionComponent<ContainerProps> = (props) => {
  const params = useParams<{ id: string }>();
  return (
    <ConnectedTeamSettings
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      params={params}
    />
  );
};

export default TeamSettingsContainer;
